// Application constants

// Photo layout modes
export type PhotoLayout = 'square' | 'waterfall' | 'river';

export const DEFAULT_PHOTO_LAYOUT: PhotoLayout = 'square';

export const LAYOUT_OPTIONS: readonly { value: PhotoLayout; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'waterfall', label: 'Waterfall' },
  { value: 'river', label: 'River' },
] as const;

// River layout constants
export const RIVER_LAYOUT = {
  TARGET_ROW_HEIGHT_MOBILE: 200,
  TARGET_ROW_HEIGHT_DESKTOP: 250,
  TARGET_ROW_HEIGHT_LARGE_DESKTOP: 320,
  GAP: 4,
} as const;

// Waterfall layout constant
export const WATERFALL_LAYOUT = {
  ROW_HEIGHT: 200,
  ROW_HEIGHT_LARGE_DESKTOP: 260,
} as const;

// Feature flags
export const FEATURE_FLAGS = {
  STREAMING_UPLOAD: true, // Enable streaming upload for better performance with large files
  LEGACY_BASE64_UPLOAD: true // Keep legacy base64 upload for backward compatibility
} as const;

// API URLs
export const API_URLS = {
  DEVELOPMENT: 'http://localhost:8080',
  STAGING: 'https://private-photos-api-staging.blackwater-262a2f6d.canadacentral.azurecontainerapps.io',
  PRODUCTION: 'https://private-photos-api.bluewave-ab9f378a.canadacentral.azurecontainerapps.io',
  LOCAL: 'http://localhost:8080'
} as const;

// Error messages
export const ERROR_MESSAGES = {
  ALBUMS_LOAD_FAILED: 'Failed to load albums',
  PHOTOS_LOAD_FAILED: 'Failed to load photos',
  ALBUM_CREATE_FAILED: 'Failed to create album',
  ALBUM_UPDATE_FAILED: 'Failed to update album',
  ALBUM_DELETE_FAILED: 'Failed to delete album',
  PHOTO_DELETE_FAILED: 'Failed to delete photo',
  PHOTO_ADD_FAILED: 'Failed to add photos',
  AUTHENTICATION_REQUIRED: 'Authentication required',
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  UNKNOWN_ERROR: 'Unknown error occurred'
} as const;

// Date formatting
export const DATE_FORMAT_OPTIONS = {
  LONG: {
    year: 'numeric' as const,
    month: 'long' as const,
    day: 'numeric' as const
  }
} as const;

// Grid layout
export const GRID_CLASSES = {
  ALBUM_GRID: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-5 4xl:grid-cols-6 gap-6 3xl:gap-8',
  PHOTO_GRID: 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6 3xl:grid-cols-7 4xl:grid-cols-8 gap-3 sm:gap-4 3xl:gap-5 4xl:gap-6'
} as const;

// Animation classes
export const ANIMATION_CLASSES = {
  SLIDE_OUT_LEFT: 'animate-slide-out-left',
  SLIDE_OUT_RIGHT: 'animate-slide-out-right',
  SLIDE_IN_RIGHT: 'animate-slide-in-right',
  SLIDE_IN_LEFT: 'animate-slide-in-left'
} as const;

// Search
export const SEARCH_CONSTANTS = {
  DEBOUNCE_MS: 300,
  PLACEHOLDERS: {
    ALBUMS: 'Search albums...',
    FRIENDS: 'Search friends...',
    CONTACTS: 'Search contacts...'
  }
} as const;

// Friends paging
export const FRIENDS_CONSTANTS = {
  PAGE_SIZE: 20
} as const;

// Social page
export const SOCIAL_CONSTANTS = {
  ACTIVITY_PAGE_SIZE: 20
} as const;

// Albums paging
export const ALBUMS_CONSTANTS = {
  PAGE_SIZE: 12
} as const;

// Scroll index sidebar
// TOP_SAFE_PX is now treated as a *floor*. The actual top is computed at runtime
// from the rendered FloatingToolbar's bottom (see ScrollIndex.tsx) so adding
// more floating controls cannot regress overlap.
export const SCROLL_INDEX_CONSTANTS = {
  TOP_SAFE_PX: 260,     // min distance from viewport top before scrollbar starts
  BOTTOM_SAFE_PX: 40,   // min distance from viewport bottom
  ITEM_HEIGHT_PX: 20    // estimated height per button (text-xs + py-0.5 + gap-0.5)
} as const;

// Sticky top navigation height (Navigation.tsx uses h-16 = 64px). Used by scroll
// restoration to avoid placing restored cards under the sticky nav bar.
export const LAYOUT_CONSTANTS = {
  STICKY_NAV_HEIGHT_PX: 64,
  RESTORE_OFFSET_EXTRA_PX: 16,
} as const;

// Album filters
export const FILTER_CONSTANTS = {
  VISIBILITY_OPTIONS: ['Private', 'AllFriends', 'Custom'] as const,
  VISIBILITY_LABELS: {
    Private: 'Private',
    AllFriends: 'All Friends',
    Custom: 'Custom'
  } as const,
  DEFAULT_FILTERS: {
    visibility: [] as string[],
    dateFrom: '' as string,
    dateTo: '' as string
  }
} as const;

// Geocoding (Nominatim / OpenStreetMap)
export const GEOCODING_CONSTANTS = {
  BASE_URL: 'https://nominatim.openstreetmap.org/search',
  USER_AGENT: 'Private-Photos-App',
  DEBOUNCE_MS: 500,
  RESULT_LIMIT: 5,
} as const;

// Map
export const MAP_CONSTANTS = {
  FLY_TO_ZOOM: 12,
} as const;